import { useState } from "react";
import { Link } from "@inertiajs/react";
import { Image as ImageIcon, User, Globe, Twitch, Save } from "lucide-react";
import { Page, Status } from "../pages/Pages";
import TiptapEditor from "./TiptapEditor";

type PageFormProps = {
    onSubmit: (data: any) => void;
    page?: Page;

};

export default function PageForm({ onSubmit, page }: PageFormProps) {
    const isNew = page === null || page === undefined;

    const [form, setForm] = useState({
        title: page?.title ?? "",
        shortDescription: page?.short_description ?? "",
        content: page?.content ?? "",
        status: page?.status ?? Status.Draft,
    });

    function updateField(key: string, value: string | number) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        onSubmit(form);
    }

    const inputClass =
        "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100";

    const labelClass = "mb-2 block text-sm font-medium text-gray-700";

    return (
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-4xl">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-6 py-5 sm:px-8">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-900">
                                {isNew ? "Create Page" : "Edit Page"}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Manage page content, status, and other information.
                            </p>
                        </div>

                        <Link
                            href="/admin/pages"
                            className="inline-flex items-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        >
                            Cancel
                        </Link>
                    </div>
                </div>

                <div className="grid gap-8 px-6 py-6 sm:px-8 lg:grid-cols-[1fr_320px]">
                    <div className="space-y-8">
                        <section className="space-y-5">
                            <div className="flex items-center gap-2">
                                <User size={18} className="text-gray-500" />
                                <h3 className="text-base font-semibold text-gray-900">Basic info</h3>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Title</label>
                                    <input
                                        type="text"
                                        value={form.title}
                                        onChange={(e) => updateField("title", e.target.value)}
                                        className={inputClass}
                                        placeholder="Page Title"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Short Description</label>
                                <input
                                    type="text"
                                    value={form.shortDescription}
                                    onChange={(e) => updateField("shortDescription", e.target.value)}
                                    className={inputClass}
                                    placeholder="Short Description"
                                />
                            </div>

                            <TiptapEditor value={form.content} onChange={(content) => updateField("content", content)} />
                        </section>
                    </div>

                </div>

                <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
                    <Link
                        href="/admin/pages"
                        className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                        Cancel
                    </Link>

                    <button
                        type="submit"
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                    >
                        <Save size={16} />
                        {isNew ? "Create Page" : "Save Changes"}
                    </button>
                </div>
            </div>
        </form>
    );
}